import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { SchoolPublic, SchoolOpponentPublic, SchoolSearchResult, LeadSubmissionPublic, SportPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { safeHref, safeHostname } from "@/lib/safe-url";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pencil, Calendar, CalendarDays, Users, Trash2, MessageSquare, FileText, MapPin, Globe, ExternalLink, Swords, X, Plus, Phone, GraduationCap, BookOpen, Building, Hash, Shield, Zap, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LeadSubmissionsTable } from "@/components/lead-submissions-table";
import { SchoolOffersSection } from "@/components/school-offers-section";
import { SchoolCashbackSection } from "@/components/school-cashback-section";
import { useAuth } from "@/lib/auth-context";
import { PinButton } from "@/components/pin-button";
import { KebabMenu } from "@/components/kebab-menu";
import { FavoriteSchoolButton } from "@/components/favorite-school-button";
import { ListBackButton } from "@/components/list-back-button";
import { SendMessageButton } from "@/components/send-message-button";
import { DirectMessageDialog } from "@/components/direct-message-dialog";
import { DocumentList } from "@/components/document-list";
import { SchoolCrest } from "@/components/school-crest";

// School detail page

export default function SchoolDetailPage() {
  const { user: authUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canBroadcast = authUser?.roles.some((r) => r.role_type === "super_admin" || r.role_type === "school_admin") ?? false;
  const [school, setSchool] = useState<SchoolPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [directMsgOpen, setDirectMsgOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    api
      .getSchool(id)
      .then((res) => setSchool(res.school))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load school"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!id || !confirm("Delete this school? This action cannot be undone.")) return;
    try {
      await api.deleteSchool(id);
      navigate("/schools");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete school");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="school-detail" data-state="loading">Loading...</p>;
  }

  if (error) {
    return <p className="text-sm text-destructive" data-page="school-detail" data-state="error" data-error>{error}</p>;
  }

  if (!school) {
    return <p className="text-muted-foreground" data-page="school-detail" data-state="empty">School not found</p>;
  }

  const schoolTypeColorClass = school.school_type
    ? ({
        P: "bg-green-100 text-green-800",
        S: "bg-blue-100 text-blue-800",
        C: "bg-purple-100 text-purple-800",
        I: "bg-amber-100 text-amber-800",
      } as Record<string, string>)[school.school_type.id] ?? "bg-muted"
    : "";

  return (
    <div className="flex flex-col gap-6" data-page="school-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          <ListBackButton to="/schools" label="Schools" />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-3">
            <SchoolCrest
              name={school.display_name ?? school.name}
              logoUrl={school.logo_url}
              primaryColor={school.primary_color}
              size={48}
            />
            <h1 className="text-lg font-semibold sm:text-2xl">{school.display_name ?? school.name}</h1>
            {school.display_name && school.display_name !== school.name && (
              <span className="hidden text-sm text-muted-foreground sm:inline">({school.name})</span>
            )}
            {school.school_type && (
              <>
                {/* Mobile: compact single-letter chip (P/S/C/I) instead of the spelled-out name */}
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold sm:hidden ${schoolTypeColorClass}`}
                  title={school.school_type.name}
                >
                  {school.school_type.id}
                </span>
                <span className={`hidden rounded-full px-3 py-0.5 text-xs font-medium sm:inline ${schoolTypeColorClass}`}>
                  {school.school_type.name}
                </span>
              </>
            )}
          </div>
          {/* Compact info line: location, phone, website */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {school.latitude != null && school.longitude != null ? (
              <a
                href={school.google_place_id ? `https://www.google.com/maps/place/?q=place_id:${school.google_place_id}` : `https://www.google.com/maps/place/${encodeURIComponent(school.name)}/@${school.latitude},${school.longitude},17z`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <MapPin className="h-3.5 w-3.5" />
                {/* 204 -- coordinates power the link, they are not reading material */}
                {school.location ?? "Map"}
              </a>
            ) : school.location ? (
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{school.location}</span>
            ) : null}
            {school.telephone && (
              <a href={`tel:${school.telephone}`} className="flex items-center gap-1 hover:text-foreground transition-colors">
                <Phone className="h-3.5 w-3.5" />{school.telephone}
              </a>
            )}
            {school.website_url && (
              safeHref(school.website_url) ? (
                <a href={safeHref(school.website_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-foreground transition-colors">
                  <Globe className="h-3.5 w-3.5" />{safeHostname(school.website_url)?.replace(/^www\./, "") ?? school.website_url}
                </a>
              ) : (
                <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{school.website_url}</span>
              )
            )}
            {school.emis_number && (
              <span className="hidden items-center gap-1 sm:flex"><Hash className="h-3.5 w-3.5" />EMIS {school.emis_number}</span>
            )}
          </div>
          {/* Stats badges */}
          <div className="flex flex-wrap gap-2 mt-1">
            {school.sector && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                school.sector === "PUBLIC" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"
              }`}>
                <Shield className="h-3 w-3" />{school.sector.charAt(0) + school.sector.slice(1).toLowerCase()}
              </span>
            )}
            {school.learner_count != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                <GraduationCap className="h-3 w-3" />{school.learner_count.toLocaleString()} learners
              </span>
            )}
            {school.educator_count != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                <BookOpen className="h-3 w-3" />{school.educator_count} educators
              </span>
            )}
            {school.quintile != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                Q{school.quintile}
              </span>
            )}
            {school.no_fee_school && (
              <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">No Fee</span>
            )}
            {school.district && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                <Building className="h-3 w-3" />{school.district}
              </span>
            )}
            {school.status && school.status !== "OPEN" && (
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">{school.status}</span>
            )}
          </div>
          {/* Address (compact, only if present) */}
          {school.street_address && (
            <p className="text-xs text-muted-foreground mt-0.5">{school.street_address}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <FavoriteSchoolButton schoolId={school.id} schoolName={school.display_name ?? school.name} />
          <PinButton pinType="school" entityType="School" entityId={school.id} />
          {canBroadcast && (
            <SendMessageButton
              contextType="school"
              contextId={school.id}
              contextLabel={school.name}
              variant="outline"
              showLabel
            />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDirectMsgOpen(true)}
            title="Direct message"
            data-action="direct-message"
          >
            <MessageSquare className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Direct Message</span>
          </Button>
          {school._actions.includes("edit") && (
            <Link
              to={`/schools/${school.slug}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Edit</span>
            </Link>
          )}
          {school._actions.includes("delete") && (
            <KebabMenu>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start text-destructive hover:text-destructive"
                onClick={handleDelete}
                data-action="delete"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
            </KebabMenu>
          )}
        </div>
      </div>

      <Card data-region="quick-links">
        <CardHeader>
          <CardTitle className="text-base">Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to={`/schools/${school.slug}/events`}
              className="flex items-center gap-3 rounded-md border p-3 transition-colors hover:bg-muted"
            >
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Events</p>
                <p className="text-sm text-muted-foreground">
                  Matches, practices &amp; tryouts
                </p>
              </div>
            </Link>
            <Link
              to={`/schools/${school.slug}/calendar`}
              className="flex items-center gap-3 rounded-md border p-3 transition-colors hover:bg-muted"
            >
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Calendar</p>
                <p className="text-sm text-muted-foreground">
                  Visual schedule of events
                </p>
              </div>
            </Link>
            <Link
              to={`/schools/${school.slug}/users`}
              className="flex items-center gap-3 rounded-md border p-3 transition-colors hover:bg-muted"
            >
              <Users className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Users</p>
                <p className="text-sm text-muted-foreground">
                  School admins &amp; coaches
                </p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
      {school._actions.includes("edit") && <OpponentSection schoolId={school.id} />}

      <Card data-region="documents">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentList schoolId={school.id} />
        </CardContent>
      </Card>
      {school._actions.includes("edit") && <SchoolPoliciesSection schoolId={school.id} />}
      {school._actions.includes("edit") && <SchoolSportsSection schoolId={school.id} />}

      {(school._actions.includes("edit") || authUser?.roles.some((r) => r.role_type === "school_admin" && r.scope_id === school.id)) && (
        <SchoolLeadsSection schoolId={school.id} />
      )}

      <SchoolOffersSection schoolId={school.id} />

      <SchoolCashbackSection
        schoolId={school.id}
        hasAccess={
          (authUser?.roles.some(
            (r) => r.role_type === "super_admin"
              || (r.role_type === "school_admin" && r.scope_id === school.id)
              || r.role_type === "org_admin",
          )) ?? false
        }
      />

      {directMsgOpen && (
        <DirectMessageDialog
          schoolId={school.id}
          schoolName={school.name}
          onClose={() => setDirectMsgOpen(false)}
        />
      )}
    </div>
  );
}

// ─── School Policies Section (144) ──────────────────────────────

function SchoolPoliciesSection({ schoolId }: { schoolId: string }) {
  const [coachesCanAdd, setCoachesCanAdd] = useState<boolean | null>(null);
  // 162 -- cross-team coaching access (default on)
  const [crossTeam, setCrossTeam] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getSchoolSettings(schoolId)
      .then((res) => {
        const flag = res.settings.find((s) => s.key === "players.coaches_can_add");
        setCoachesCanAdd(flag ? flag.value === "true" : true);
        const cross = res.settings.find((s) => s.key === "teams.coaches_cross_team_access");
        setCrossTeam(cross ? cross.value === "true" : true);
      })
      .catch(() => { setCoachesCanAdd(true); setCrossTeam(true); });
  }, [schoolId]);

  async function toggle() {
    if (coachesCanAdd === null) return;
    const next = !coachesCanAdd;
    setSaving(true);
    setError(null);
    try {
      await api.updateSchoolSetting(schoolId, "players.coaches_can_add", String(next));
      setCoachesCanAdd(next);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function toggleCrossTeam() {
    if (crossTeam === null) return;
    const next = !crossTeam;
    setSaving(true);
    setError(null);
    try {
      await api.updateSchoolSetting(schoolId, "teams.coaches_cross_team_access", String(next));
      setCrossTeam(next);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-region="school-policies">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Policies
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4" data-field="coaches-can-add">
          <div>
            <p className="text-sm font-medium">Coaches can add players</p>
            <p className="text-sm text-muted-foreground">
              When off, only parents and administrators can create or import players at this school.
            </p>
          </div>
          {coachesCanAdd === null ? (
            <span className="text-sm text-muted-foreground">…</span>
          ) : (
            <Button
              type="button"
              variant={coachesCanAdd ? "default" : "outline"}
              onClick={toggle}
              disabled={saving}
              data-action="toggle-coaches-can-add"
              data-state={coachesCanAdd ? "on" : "off"}
            >
              {saving ? "Saving…" : coachesCanAdd ? "On" : "Off"}
            </Button>
          )}
        </div>
        {/* 162 -- cross-team coaching access */}
        <div className="mt-4 flex items-center justify-between gap-4 border-t border-border pt-4" data-field="coaches-cross-team">
          <div>
            <p className="text-sm font-medium">Coaches can manage any team</p>
            <p className="text-sm text-muted-foreground">
              When on, any coach at this school can view and edit every team (for administrative continuity), not only their assigned teams.
            </p>
          </div>
          {crossTeam === null ? (
            <span className="text-sm text-muted-foreground">…</span>
          ) : (
            <Button
              type="button"
              variant={crossTeam ? "default" : "outline"}
              onClick={toggleCrossTeam}
              disabled={saving}
              data-action="toggle-coaches-cross-team"
              data-state={crossTeam ? "on" : "off"}
            >
              {saving ? "Saving…" : crossTeam ? "On" : "Off"}
            </Button>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

// ─── School Sports Section ──────────────────────────────────────

function SchoolSportsSection({ schoolId }: { schoolId: string }) {
  const [allSports, setAllSports] = useState<SportPublic[] | null>(null);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.getSports(), api.getSchoolSports(schoolId)])
      .then(([sportsRes, schoolRes]) => {
        setAllSports(sportsRes.sports);
        setEnabledIds(new Set(schoolRes.sports.map((s) => s.sport_id)));
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load sports"));
  }, [schoolId]);

  async function toggle(sportId: string) {
    const isOn = enabledIds.has(sportId);
    setSavingId(sportId);
    setError(null);
    try {
      if (isOn) {
        await api.disableSchoolSport(schoolId, sportId);
        setEnabledIds((prev) => {
          const next = new Set(prev);
          next.delete(sportId);
          return next;
        });
      } else {
        await api.enableSchoolSport(schoolId, sportId);
        setEnabledIds((prev) => new Set(prev).add(sportId));
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <Card data-region="school-sports">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="h-4 w-4" />
          Sports
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Choose which sports this school offers. Only enabled sports appear when creating teams, events, and players.
        </p>
        {allSports === null ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : allSports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sports are available system-wide.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-field="sports">
            {allSports.map((sport) => {
              const on = enabledIds.has(sport.id);
              return (
                <Button
                  key={sport.id}
                  type="button"
                  size="sm"
                  variant={on ? "default" : "outline"}
                  onClick={() => toggle(sport.id)}
                  disabled={savingId === sport.id}
                  data-action="toggle-sport"
                  data-sport={sport.id}
                  data-state={on ? "on" : "off"}
                >
                  {on ? <X className="mr-1 h-3.5 w-3.5" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
                  {sport.name}
                </Button>
              );
            })}
          </div>
        )}
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

// ─── School Leads Section (067) ─────────────────────────────────

function SchoolLeadsSection({ schoolId }: { schoolId: string }) {
  const [leads, setLeads] = useState<LeadSubmissionPublic[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSchoolLeads(schoolId)
      .then((res) => { setLeads(res.leads); setTotal(res.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return null;

  return (
    <Card data-region="leads">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Sponsor Leads
        </CardTitle>
        <Badge variant="secondary">{total} total</Badge>
      </CardHeader>
      <CardContent>
        <LeadSubmissionsTable leads={leads} total={total} contextColumn="sponsor" />
      </CardContent>
    </Card>
  );
}

// ─── Opponent Management Section ─────────────────────────────────

function OpponentSection({ schoolId }: { schoolId: string }) {
  const [opponents, setOpponents] = useState<SchoolOpponentPublic[]>([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SchoolSearchResult[]>([]);
  const [showDrop, setShowDrop] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.getSchoolOpponents(schoolId).then((res) => setOpponents(res.opponents)).catch(() => {}).finally(() => setLoading(false));
  }, [schoolId]);

  // Search schools with debounce
  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      api.searchSchools(search.trim()).then((res) => setResults(res.schools));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const existingOpponentIds = useMemo(() => new Set(opponents.map((o) => o.opponent_school_id)), [opponents]);
  const filtered = results.filter((s) => !existingOpponentIds.has(s.id));

  async function addOpponent(ns: SchoolSearchResult) {
    setSearch("");
    setShowDrop(false);
    const res = await api.addSchoolOpponent(schoolId, ns.id);
    setOpponents((prev) => [...prev, res.opponent].sort((a, b) => a.opponent_name.localeCompare(b.opponent_name)));
  }

  async function removeOpponent(opponentSchoolId: string) {
    setOpponents((prev) => prev.filter((o) => o.opponent_school_id !== opponentSchoolId));
    await api.removeSchoolOpponent(schoolId, opponentSchoolId);
  }

  return (
    <Card data-region="opponents">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Swords className="h-4 w-4" />
          Opponents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div ref={ref} className="relative mb-3 max-w-sm">
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setShowDrop(true); }}
            onFocus={() => { if (search.trim().length >= 2) setShowDrop(true); }}
            placeholder="Search schools..." className="h-8 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter" && filtered.length === 1) { e.preventDefault(); addOpponent(filtered[0]); }
              if (e.key === "Escape") setShowDrop(false);
            }}
          />
          {showDrop && search.trim().length >= 2 && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-background shadow-md">
              {filtered.map((s) => (
                <button key={s.id} type="button" onClick={() => addOpponent(s)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted">
                  <span className="font-medium">{s.name}</span>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">{s.town ? `${s.town}, ` : ""}{s.province ?? ""}</span>
                </button>
              ))}
              {filtered.length === 0 && results.length > 0 && <div className="px-3 py-2 text-sm text-muted-foreground">All matches already added</div>}
              {results.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No schools found</div>}
            </div>
          )}
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : opponents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No opponents added yet. Search above to add schools you regularly play against.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {opponents.map((o) => (
              <span key={o.opponent_school_id} className="group/chip inline-flex items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-800 whitespace-nowrap">
                {o.opponent_name}
                {o.town && <span className="font-normal opacity-60">({o.town})</span>}
                <button type="button" title={`Remove ${o.opponent_name}`} onClick={() => removeOpponent(o.opponent_school_id)}
                  className="hidden hover:text-destructive group-hover/chip:inline-flex">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
